import React, { useState, useMemo } from 'react';
import { useDeepCompareEffect } from 'react-use-v13';
import {
  TextCollectionField,
  TextCollectionFieldType,
  UnitField,
} from '@scale/shared/types/params/textcollection';
import { FieldTypeSchemaMap } from '@scale/shared/taskTemplate/textCollectionSchemas';
import _ from 'lodash';
import ArrayBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/ArrayBuilder';
import { ChunkColor } from 'react-scale/dashboard.scale.com/components/SelfServe/Onboarding/Task/components/TaxonomyEditor';
import { Button, Icon, SelectMenu, Typography, Divider, Skeleton } from '@scale/scaleui';
import {
  useGetQuestionBankByProjectId,
  useGetQuestionBankQuestions,
} from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { QuestionDTO } from '@scale/shared/questionBank/dto/Question';
import { useProjectId } from 'react-scale/corp/views/CorpProjectGroupManager/hooks/useProjectId';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { IDeliveryBuilderConfig } from '@scale/shared/types/config';
import type { TypeBuilderMapType } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemasType';

interface FieldsBuilderProps {
  name: string;
  existingValue?: Array<TextCollectionField>;
  updateProperty: (arg: Array<TextCollectionField>) => void;
  required?: boolean;
  labelsToColors?: Record<string, ChunkColor>;
  toggleLabel?: (l: string) => void;
  showDeprecatedOptions?: boolean;
  restrictedFields?: Array<UnitField>; // only allow deleting and resetting to restricted fields
  taskTemplateParams?: Record<string, any>;
  stepId?: string;
  deliveryBuilderConfig?: IDeliveryBuilderConfig;
  typeBuilderMap: TypeBuilderMapType;
}

interface FieldOption {
  id: TextCollectionFieldType;
  name: string;
}

export const normalOptions: Array<FieldOption> = [
  { id: TextCollectionFieldType.Text, name: 'Text' },
  { id: TextCollectionFieldType.Boolean, name: 'Boolean' },
  { id: TextCollectionFieldType.Number, name: 'Number' },
  { id: TextCollectionFieldType.Category, name: 'Category' },
  { id: TextCollectionFieldType.Autocomplete, name: 'Dropdown Menu' },
  { id: TextCollectionFieldType.FileUpload, name: 'File Upload' },
  { id: TextCollectionFieldType.AudioRecording, name: 'Audio Recording' },
  { id: TextCollectionFieldType.MultiChannelAudioUpload, name: 'Multi-Channel Audio Upload' },
  { id: TextCollectionFieldType.Markdown, name: 'Markdown' },
  { id: TextCollectionFieldType.WorkspaceContainer, name: 'Workspace Container' },
  { id: TextCollectionFieldType.Tool, name: 'Tool' },
  { id: TextCollectionFieldType.Instruction, name: 'Instruction' },
  { id: TextCollectionFieldType.Rubric, name: 'Rubric' },
];

const deprecatedOptions: Array<FieldOption> = [
  { id: TextCollectionFieldType.Datetime, name: 'Datetime' },
  { id: TextCollectionFieldType.TimeRange, name: 'Time Range' },
  { id: TextCollectionFieldType.Select, name: 'Select' },
  { id: TextCollectionFieldType.BoundingBox, name: 'Bounding Box' },
  { id: TextCollectionFieldType.FieldSet, name: 'FieldSet' },
  { id: TextCollectionFieldType.RankingOrder, name: 'Ranking' },
  { id: TextCollectionFieldType.Code, name: 'Code' },
  { id: TextCollectionFieldType.QueryableTextAndResults, name: 'Queryable Text And Results' },
  { id: TextCollectionFieldType.JSON, name: 'JSON' },
  { id: TextCollectionFieldType.WfeButton, name: 'WFE Button' },
];

const miniformOptions: Array<FieldOption> = [
  { id: TextCollectionFieldType.Form, name: 'Miniform' },
];

const FieldsBuilder: React.FC<FieldsBuilderProps> = ({
  name,
  existingValue = [],
  updateProperty,
  labelsToColors,
  toggleLabel,
  showDeprecatedOptions,
  restrictedFields,
  taskTemplateParams,
  stepId,
  deliveryBuilderConfig,
  typeBuilderMap,
}) => {
  const projectId = useProjectId();
  const fields = existingValue;
  const [selectedType, setSelectedType] = useState<TextCollectionFieldType>(
    TextCollectionFieldType.Text,
  );
  const [selectedQuestion, setSelectedQuestion] = useState<string>('');
  const isQuestionBankEnabled = useUnleashFeatureFlag('question-bank', { projectId }, false);
  const useChatTaskCustomerVisibilityTab = useUnleashFeatureFlag(
    'chat-task-customer-visibility-tab',
    { projectId },
    false,
  );
  const { data: questionBank, isFetching: isQuestionBankFetching } = useGetQuestionBankByProjectId(
    projectId || '',
  );
  const { data: questions, isFetching: isQuestionsFetching } = useGetQuestionBankQuestions(
    questionBank?.id,
  );

  const hiddenFields: string[] = useMemo(() => {
    if (!useChatTaskCustomerVisibilityTab) {
      return [];
    }
    return (
      deliveryBuilderConfig?.fieldsHiddenFromCustomer
        ?.filter((fh: { stepId: string; fieldId: string }) => fh.stepId === stepId)
        .map((fh: { stepId: string; fieldId: string }) => fh.fieldId) ?? []
    );
  }, [useChatTaskCustomerVisibilityTab, deliveryBuilderConfig, stepId]);

  const [fieldOptions, setFieldOptions] = useState<Array<FieldOption>>([
    ...normalOptions,
    ...miniformOptions,
  ]);

  const addField = (type: TextCollectionFieldType, questionField?: QuestionDTO) => {
    const newFields = [...fields];
    const valuePlaceholder = `field_${fields.length + 1}`;

    if (questionField) {
      newFields.push({
        ...questionField.params,
      });
    } else {
      newFields.push({
        type,
        field_id: valuePlaceholder,
        title: 'Placeholder Title',
      } as UnitField);
    }

    updateProperty(newFields);
  };

  useDeepCompareEffect(() => {
    if (name) {
      setFieldOptions([...normalOptions, ...(showDeprecatedOptions ? deprecatedOptions : [])]); // For subfields
    } else if (fields.length === 0) {
      setFieldOptions([
        ...normalOptions,
        ...(showDeprecatedOptions ? deprecatedOptions : []),
        ...miniformOptions,
      ]);
    } else if (fields.map(field => field.type).includes(TextCollectionFieldType.Form)) {
      setFieldOptions(miniformOptions);
    } else {
      setFieldOptions([...normalOptions, ...(showDeprecatedOptions ? deprecatedOptions : [])]);
    }
  }, [fields]);

  return (
    <div className="scaleui scaleui-tailwind scale-theme">
      {name && <div className="my-2 text-lg">{name}</div>}
      <ArrayBuilder
        typeBuilderMap={typeBuilderMap}
        elements={fields}
        setElements={updateProperty}
        getKey={field => field.field_id}
        getIsHidden={field => hiddenFields.some((fieldId: string) => fieldId === field.field_id)}
        getTitle={field => `${_.capitalize(field.type)} - ${field.field_id}`}
        getSchema={field => FieldTypeSchemaMap[field.type]}
        getTags={field => {
          const tags = [];
          if (field.required) tags.push('required');
          if ('save_to_task_feedback' in field && field.save_to_task_feedback)
            tags.push('save to task feedback');
          return tags.length ? tags : undefined;
        }}
        getCloneFn={field => ({
          ..._.cloneDeep(field),
          field_id: `${field.field_id}_clone`,
          title: `${field.title} Clone`,
        })}
        labelsToColors={labelsToColors}
        toggleLabel={toggleLabel}
        fullWidth
        restrictFields={!!restrictedFields}
        taskTemplateParams={taskTemplateParams}
        fields={fields}
        getWarningOnModify={propertyName =>
          propertyName === 'Field ID'
            ? "Do not modify Field IDs after they've been saved, this will cause errors in backfills, deliveries, and more. Modify the Title instead."
            : undefined
        }
      />
      {!restrictedFields && (
        <div className="flex flex-row items-center flex-wrap gap-2">
          <div className="flex flex-row items-center gap-2">
            <Typography.Subtitle3>Default Fields:</Typography.Subtitle3>
            <SelectMenu
              className="w-48"
              options={fieldOptions}
              selectedOptionId={selectedType}
              setSelected={value => {
                setSelectedType(value as TextCollectionFieldType);
              }}
            />
            <div className="ml-2">
              <Icon
                hasBackground
                icon={['fas', 'plus']}
                onClick={() => addField(selectedType)}
                size="md"
                variant="primary"
              />
            </div>
          </div>
          <Divider direction="vertical" />
          {isQuestionBankEnabled && (
            <div className="flex flex-row items-center gap-2">
              <Typography.Subtitle3>Question Bank:</Typography.Subtitle3>
              {isQuestionBankFetching || isQuestionsFetching ? (
                <Skeleton className="w-60 h-9" />
              ) : (
                <SelectMenu
                  className="w-60"
                  options={
                    questions?.map(q => {
                      const fieldOption = normalOptions.find(opt => opt.id === q.params.type);
                      return {
                        id: q.id,
                        name: `${q.name} (${fieldOption?.name ?? q.params.type})`,
                      };
                    }) ?? []
                  }
                  selectedOptionId={selectedQuestion}
                  setSelected={value => {
                    setSelectedQuestion(value as string);
                  }}
                  placeholder="Select from Question Bank"
                />
              )}
              <div className="ml-2">
                <Icon
                  hasBackground
                  icon={['fas', 'plus']}
                  onClick={() => {
                    const question = questions?.find(q => q.id === selectedQuestion);
                    if (question) {
                      addField(question.params.type, question);
                    }
                  }}
                  size="md"
                  variant="primary"
                  disabled={!selectedQuestion || isQuestionBankFetching || isQuestionsFetching}
                />
              </div>
            </div>
          )}
        </div>
      )}
      {restrictedFields && (
        <div className="flex items-center">
          <div className="ml-2 flex flex-row">
            <Button
              disabled={existingValue?.length === restrictedFields.length}
              variant="danger"
              onClick={() => updateProperty(restrictedFields)}
            >
              Reset to Standard Fields
            </Button>
            {existingValue?.length !== restrictedFields.length && (
              <div className="ml-2 mt-1 text-neutral-500">
                <strong>Removed fields: </strong>
                {restrictedFields
                  .filter(f => !existingValue?.map(f => f.field_id).includes(f.field_id))
                  .map(f => f.field_id)
                  .join(', ')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldsBuilder;
